
import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, 
  ShieldCheck, 
  Smartphone, 
  Zap, 
  Globe, 
  Lock, 
  Menu, 
  X,
  ChevronDown,
  Repeat,
  Copy,
  CheckCircle2,
  Activity,
  Headset,
  Webhook,
  Building2,
  Rocket,
  ChevronRight,
  Send,
  Loader2,
  BarChart3,
  Twitter,
  MessageCircle,
  Instagram,
  Youtube,
  Maximize2,
  TrendingUp,
  Coins
} from 'lucide-react';

// Custom TikTok Icon
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z"/>
  </svg>
);

// CONFIGURAÇÕES GERAIS
const REDIRECT_URL = "https://app.defibank.digital"; 
const DBK_CONTRACT = "0x03457450573FfCae9a3cD5cA41597dF3787A570D";
const LISTING_URL = "https://listing.defibank.digital";

const SOCIAL_LINKS = {
  telegram: "https://t.me/defibankofficial",
  whatsapp: "https://chat.whatsapp.com/G2URJlZvs4MGFqrUfGMqOp",
  x: "http://x.com/defibankoficial",
  instagram: "https://instagram.com/defibankoficial",
  tiktok: "https://www.tiktok.com/@defi.bank",
  youtube: "https://www.youtube.com/@DefiBankDigital"
};

interface MockupItem {
  id: number;
  title: string;
  description: string;
  image: string;
  benefit: string;
}

const mockups: MockupItem[] = [
  { id: 1, title: 'Login Seguro Web3', description: 'Interface limpa e intuitiva para retenção máxima de usuários cripto.', image: 'https://i.imgur.com/QKieW99.jpeg', benefit: 'Onboarding veloz que reduz o churn de novos usuários.' },
  { id: 2, title: 'Compra USDT via PIX', description: 'Liquidez instantânea do mundo real para o seu token ou stablecoin.', image: 'https://i.imgur.com/52ListL.jpeg', benefit: 'Fim das barreiras técnicas de entrada para investidores brasileiros.' },
  { id: 3, title: 'Smart Swap DeFi', description: 'Trocas descentralizadas com a melhor cotação e zero slippage excessivo.', image: 'https://i.imgur.com/U4ibzK3.jpeg', benefit: 'Experiência DeFi completa sem sair da custódia do usuário.' },
  { id: 5, title: 'Banner Promocional Cripto', description: 'Marketing direto no ponto de venda para ativos digitais.', image: 'https://i.imgur.com/GPhBU0N.jpeg', benefit: 'Aumente o volume de trading com exposição segmentada.' },
  { id: 7, title: 'Jhow AI Assistant', description: 'Seu projeto integrado ao Jhow, assistente de IA da Defi Bank.', image: 'https://i.imgur.com/Qvxd1DU.jpeg', benefit: 'Conecte seu projeto ao estilo de vida dos usuários.' },
];

const translations = {
  pt: {
    nav: { pix: "Pix - USDT", card: "Cartão", b2b: "Empresas", token: "$DBK", staking: "Listagem", wallet: "ABRIR APP" },
    hero: {
      badge: "Sua Carteira Web3: Liberdade e Autonomia",
      title_1: "Transforme seu",
      title_accent: "Pix em Dólar",
      title_2: "em segundos.",
      desc: "A DeFi Bank é a melhor forma de dolarizar seu patrimônio com autocustódia total. Use Pix para comprar USDT, gaste no Cartão Visa sem IOF e ganhe recompensas em $DBK.",
      cta: "CRIAR CONTA GRÁTIS",
      custody: "100% Autocustódia",
      yield: "Rendimento 9.5% a.a."
    },
    stats: {
      remittances: "Volume Processado",
      users: "Usuários Ativos",
      stake: "$DBK em Staking",
      fee: "Taxa de Depósito"
    },
    assets: {
      badge: "OPORTUNIDADE EM ATIVOS DIGITAIS",
      title: "Invista como as",
      title_accent: "Baleias do Mercado",
      desc: "Acesse os melhores ativos do mundo. Do Bitcoin ao Ouro Tokenizado (RWA), com a segurança da blockchain e liquidez imediata via Pix.",
      cta: "COMEÇAR A INVESTIR AGORA",
      plus: "+60 Ativos em Blockchain",
      items: {
        usdt: "Dólar Digital (Tether)",
        xaum: "Ouro Real Tokenizado",
        brz: "Real Digital (BRZ)",
        xrp: "Pagamentos Globais",
        btc: "Reserva de Valor",
        eth: "Infraestrutura Web3",
        usdc: "Dólar Regulado",
        bnb: "Ecossistema BSC",
        usd1: "Stablecoin Premium"
      }
    },
    partners: {
      badge: "ECOSSISTEMA DEFI BANK",
      title: "Apoiado pelas Maiores da Web3",
      desc: "Integrações nativas com protocolos líderes para garantir sua segurança e a melhor liquidez do mercado brasileiro."
    },
    pix: {
      badge: "PIX PARA USDT INSTANTÂNEO",
      title: "Dolarize-se Sem Bancos.",
      title_2: "O Fluxo Mais Rápido.",
      desc: "Esqueça a burocracia das corretoras tradicionais. Com a DeFi Bank, o seu Pix vira USDT direto na sua carteira não-custodial em tempo recorde.",
      cta: "COMPRAR USDT COM PIX",
      feature_1_title: "On-ramp Pix Direto",
      feature_1_desc: "Envie de qualquer banco e receba cripto na sua wallet Web3 em segundos.",
      feature_2_title: "Conversão Smart",
      feature_2_desc: "Conversão automática para dólar digital com spread reduzido e taxas transparentes."
    },
    card: {
      badge: "CARTÃO VISA CRIPTO GLOBAL",
      title: "Gaste USDT no Mundo",
      title_accent: "Com Zero IOF.",
      desc: "O cartão definitivo para quem vive em dólar. Aceito em milhões de estabelecimentos. Sem taxas abusivas e com cashback real.",
      iof_label: "Taxa IOF Zero",
      cashback_label: "Cashback em $DBK",
      cta: "SOLICITAR MEU CARTÃO VISA",
      badge_float: "Aceito Globalmente"
    },
    b2b: {
      badge: "WEB3 PARA EMPRESAS (B2B)",
      title: "Infraestrutura Financeira de Próxima Geração",
      desc: "Soluções institucionais para empresas que desejam operar com ativos digitais, stablecoins e tokenização.",
      sol_1_title: "OTC & High Volume",
      sol_1_desc: "Liquidez profunda para grandes remessas internacionais e operações corporativas.",
      sol_1_cta: "Consultar Mesa OTC",
      sol_2_title: "Gateway de Pagamento Pix-Cripto",
      sol_2_desc: "API para empresas receberem pagamentos e liquidarem em USDT automaticamente.",
      sol_2_cta: "Documentação API",
      sol_3_title: "Tokenização RWA",
      sol_3_desc: "Traga ativos do mundo real para a blockchain com conformidade e transparência.",
      sol_3_cta: "Tokenizar Ativo",
      sol_4_title: "Launchpad Estratégico",
      sol_4_desc: "Lançamento e aceleração de projetos cripto no ecossistema DeFi Bank.",
      sol_4_cta: "Submeter Projeto",
      final_title: "Escale sua operação com tecnologia Blockchain",
      final_cta: "FALAR COM UM CONSULTOR",
      final_demo: "AGENDAR DEMONSTRAÇÃO"
    },
    form: {
      title: "Contato Institucional",
      subtitle: "Nossos especialistas entrarão em contato em até 24h para entender seu desafio.",
      label_name: "Nome Completo",
      label_email: "E-mail Corporativo",
      label_phone: "WhatsApp / Telefone",
      label_comment: "Qual sua necessidade Web3?",
      placeholder_comment: "Explique como o DeFi Bank pode ajudar sua empresa...",
      submit: "ENVIAR PROPOSTA",
      sending: "PROCESSANDO...",
      success_title: "Recebemos sua Mensagem!",
      success_desc: "Um consultor sênior entrará em contato em breve.",
      close: "Fechar"
    },
    token: {
      badge: "TOKEN DE UTILIDADE $DBK",
      title: "$DBK Utility Token",
      subtitle: "Tokenomia Voltada para utilidade real",
      lead: "O $DBK foi projetado como a camada central de valor que sustenta acesso, eficiência e utilidade dentro da infraestrutura DeFi Bank.",
      p1_title: "Staking de Alto Rendimento",
      p1_phrase: "Gere renda passiva com seus ativos digitais.",
      p1_desc: "Mecanismo de staking sustentável com recompensas distribuídas aos detentores de longo prazo.",
      p2_title: "Acesso a RWAs",
      p2_phrase: "Participe de investimentos em ativos reais.",
      p2_desc: "Detentores de $DBK têm prioridade em lançamentos de imóveis e recebíveis tokenizados.",
      p3_title: "Taxas Reduzidas",
      p3_phrase: "Economize em todas as transações da wallet.",
      p3_desc: "Descontos progressivos em taxas de swap e saques baseados no seu saldo de $DBK.",
      p4_title: "Ecossistema Integrado",
      p4_phrase: "Utilidade real em pagamentos e serviços.",
      p4_desc: "Use $DBK para pagar assinaturas, taxas e garantir cashback máximo no cartão Visa.",
      contract_label: "Endereço do Contrato (BNB Chain)",
      copy: "COPIAR CONTRATO",
      copied: "COPIADO!"
    },
    security: {
      badge: "SEGURANÇA E PRIVACIDADE TOTAL",
      title: "Seu Dinheiro, Suas Chaves.",
      desc: "A DeFi Bank é uma carteira não-custodial. Isso significa que nós nunca temos acesso aos seus fundos. Você é o único dono da sua chave privada e da sua liberdade financeira.",
      item_1_title: "Criptografia AES-256 de Ponta",
      item_1_desc: "Segurança de nível militar para proteger seus dados e chaves locais.",
      item_2_title: "Proteção de Senha Dupla",
      item_2_desc: "Cada conta possui uma senha de acesso e cada endereço possui sua própria senha de assinatura de transações. Mesmo que a senha principal seja comprometida, fundos exigem senha individual.",
      item_3_title: "Autocustódia Verificada",
      item_3_desc: "O usuário pode definir senhas diferentes para cada endereço, criando camadas isoladas de proteção dentro da mesma conta.",
      shield: "CARTEIRA NÃO-CUSTODIAL",
      cert: "SEGURO & RÁPIDO"
    },
    final: {
      title: "O futuro é descentralizado.",
      title_accent: "Assuma o controle agora.",
      desc: "Junte-se a milhares de brasileiros que já saíram do sistema bancário tradicional e descobriram a verdadeira liberdade da Web3 com o DeFi Bank.",
      cta: "ABRIR MINHA CARTEIRA AGORA"
    },
    footer: {
      desc: "O DeFi Bank Wallet é a ponte definitiva entre o sistema financeiro tradicional e a nova economia Web3. Nossa missão é democratizar o acesso ao dólar digital, ouro tokenizado e serviços financeiros descentralizados com total segurança e praticidade.",
      navigate: "MAPA DO SITE",
      links: {
        pix: "Pix para USDT",
        card: "Cartão Cripto",
        b2b: "Soluções B2B",
        token: "Token $DBK",
        staking: "Listagem de Tokens"
      },
      rights: "© 2026 DEFI BANK WALLET - INFRAESTRUTURA FINANCEIRA WEB3. TODOS OS DIREITOS RESERVADOS."
    }
  },
  en: {
    nav: { pix: "Pix to USDT", card: "Card", b2b: "Institutional", token: "$DBK", staking: "Listing", wallet: "OPEN APP" },
    hero: {
      badge: "Your Web3 Wallet: Freedom & Autonomy",
      title_1: "Transform your",
      title_accent: "Pix into USDT",
      title_2: "in a few seconds.",
      desc: "DeFi Bank is the best way to dollarize your wealth with total self-custody. Use Pix to buy USDT, spend on a Visa Card with no IOF, and earn $DBK rewards.",
      cta: "CREATE FREE ACCOUNT",
      custody: "100% Non-Custodial",
      yield: "USDT Yield 9.5% APY"
    },
    stats: {
      remittances: "Processed Volume",
      users: "Active Users",
      stake: "$DBK Staked",
      fee: "Deposit Fee"
    },
    assets: {
      badge: "DIGITAL ASSET OPPORTUNITY",
      title: "Invest Like a",
      title_accent: "Market Whale",
      desc: "Access the world's best assets. From Bitcoin to Tokenized Gold (RWA), with blockchain security and immediate liquidity via Pix.",
      cta: "START INVESTING NOW",
      plus: "+60 Blockchain Assets",
      items: {
        usdt: "Digital Dollar (Tether)",
        xaum: "Tokenized Real Gold",
        brz: "Digital Real (BRZ)",
        xrp: "Global Payments",
        btc: "Store of Value",
        eth: "Web3 Infrastructure",
        usdc: "Regulated Dollar",
        bnb: "BSC Ecosystem",
        usd1: "Premium Stablecoin"
      }
    },
    partners: {
      badge: "DEFI BANK ECOSYSTEM",
      title: "Backed by Web3 Leaders",
      desc: "Native integrations with leading protocols to ensure your security and the best liquidity in the Brazilian market."
    },
    pix: {
      badge: "INSTANT PIX TO USDT",
      title: "Go Dollarized Without Banks.",
      title_2: "The Fastest Flow.",
      desc: "Forget traditional exchange bureaucracy. With DeFi Bank, your Pix turns into USDT directly in your non-custodial wallet in record time.",
      cta: "BUY USDT WITH PIX",
      feature_1_title: "Direct Pix On-ramp",
      feature_1_desc: "Send from any bank and receive crypto in your Web3 wallet in seconds.",
      feature_2_title: "Smart Exchange",
      feature_2_desc: "Automatic conversion to digital dollars with reduced spread and transparent fees."
    },
    card: {
      badge: "GLOBAL CRYPTO VISA CARD",
      title: "Spend USDT Worldwide",
      title_accent: "With Zero IOF.",
      desc: "The ultimate card for those living in dollars. Accepted at millions of locations. No hidden fees and real cashback.",
      iof_label: "Zero IOF Tax",
      cashback_label: "$DBK Cashback",
      cta: "REQUEST MY VISA CARD",
      badge_float: "Accepted Globally"
    },
    b2b: {
      badge: "WEB3 FOR BUSINESS (B2B)",
      title: "Next-Gen Financial Infrastructure",
      desc: "Institutional solutions for companies wishing to operate with digital assets, stablecoins, and tokenization.",
      sol_1_title: "OTC & High Volume",
      sol_1_desc: "Deep liquidity for large international remittances and corporate operations.",
      sol_1_cta: "Consult OTC Desk",
      sol_2_title: "Pix-Crypto Payment Gateway",
      sol_2_desc: "API for companies to receive payments and settle in USDT automatically.",
      sol_2_cta: "API Documentation",
      sol_3_title: "RWA Tokenization",
      sol_3_desc: "Bring real-world assets to the blockchain with compliance and transparency.",
      sol_3_cta: "Tokenize Asset",
      sol_4_title: "Strategic Launchpad",
      sol_4_desc: "Launch and accelerate crypto projects within the DeFi Bank ecosystem.",
      sol_4_cta: "Submit Project",
      final_title: "Scale your operation with Blockchain technology",
      final_cta: "TALK TO A CONSULTANT",
      final_demo: "SCHEDULE DEMO"
    },
    form: {
      title: "Institutional Contact",
      subtitle: "Our specialists will contact you within 24h to understand your challenge.",
      label_name: "Full Name",
      label_email: "Corporate Email",
      label_phone: "WhatsApp / Phone",
      label_comment: "What is your Web3 need?",
      placeholder_comment: "Explain how DeFi Bank can help your business...",
      submit: "SEND PROPOSAL",
      sending: "PROCESSING...",
      success_title: "Message Received!",
      success_desc: "A senior consultant will contact you shortly.",
      close: "Close"
    },
    token: {
      badge: "$DBK UTILITY TOKEN",
      title: "The Heart of the Ecosystem: $DBK",
      subtitle: "Valuation-Driven Tokenomics",
      lead: "$DBK token is not just a currency, it's exclusive access to benefits, yields, and governance within the DeFi Bank ecosystem.",
      p1_title: "High Yield Staking",
      p1_phrase: "Generate passive income with your digital assets.",
      p1_desc: "Sustainable staking mechanism with rewards distributed to long-term holders.",
      p2_title: "Access to RWAs",
      p2_phrase: "Participate in real asset investments.",
      p2_desc: "$DBK holders have priority in launches of tokenized real estate and receivables.",
      p3_title: "Reduced Fees",
      p3_phrase: "Save on all wallet transactions.",
      p3_desc: "Progressive discounts on swap and withdrawal fees based on your $DBK balance.",
      p4_title: "Integrated Ecosystem",
      p4_phrase: "Real utility in payments and services.",
      p4_desc: "Use $DBK to pay for subscriptions, fees, and ensure maximum cashback on the Visa card.",
      contract_label: "Contract Address (BNB Chain)",
      copy: "COPY CONTRACT",
      copied: "COPIED!"
    },
    security: {
      badge: "TOTAL SECURITY & PRIVACY",
      title: "Your Money, Your Keys.",
      desc: "DeFi Bank is a non-custodial wallet. This means we never have access to your funds. You are the sole owner of your private key and your financial freedom.",
      item_1_title: "Leading AES-256 Encryption",
      item_1_desc: "Military-grade security to protect your data and local keys.",
      item_2_title: "Dual Password Protection",
      item_2_desc: "One password for app access and another exclusive for signing transactions.",
      item_3_title: "Verified Self-Custody",
      item_3_desc: "Audited code and architecture ensuring only you move your assets.",
      shield: "NON-CUSTODIAL WALLET",
      cert: "AUDITED & SECURE"
    },
    final: {
      title: "The future is decentralized.",
      title_accent: "Take control now.",
      desc: "Join thousands of Brazilians who have already left the traditional banking system and discovered the true freedom of Web3 with DeFi Bank.",
      cta: "OPEN MY WALLET NOW"
    },
    footer: {
      desc: "DeFi Bank Wallet is the ultimate bridge between the traditional financial system and the new Web3 economy. Our mission is to democratize access to digital dollars, tokenized gold, and decentralized financial services with total security and practicality.",
      navigate: "SITE MAP",
      links: {
        pix: "Pix to USDT",
        card: "Crypto Card",
        b2b: "B2B Solutions",
        token: "$DBK Token",
        staking: "Token Listing"
      },
      rights: "© 2026 DEFI BANK WALLET - WEB3 FINANCIAL INFRASTRUCTURE. ALL RIGHTS RESERVED."
    }
  }
};

type TranslationSchema = typeof translations.pt;
type Language = 'pt' | 'en';

interface TranslationProps {
  t: TranslationSchema;
}

interface LanguageProps extends TranslationProps {
  lang: Language;
  setLang: (lang: Language) => void;
}

interface ModalProps extends TranslationProps {
  isOpen: boolean;
  onClose: () => void;
}

interface InstitutionalProps extends TranslationProps {
  onOpenForm: () => void;
}

interface HeroProps extends TranslationProps {
  onSelectItem: (item: MockupItem) => void;
}

const ContactModal: React.FC<ModalProps> = ({ isOpen, onClose, t }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', comment: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success'>('idle');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setTimeout(() => {
      setStatus('success');
    }, 1500);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/90 backdrop-blur-xl animate-fade-in">
      <div className="glass-card w-full max-w-xl rounded-[32px] sm:rounded-[48px] border-white/10 shadow-[0_50px_100px_rgba(0,0,0,0.9)] relative overflow-hidden">
        <button onClick={onClose} className="absolute top-6 right-6 text-zinc-500 hover:text-white transition-colors z-20" aria-label="Fechar">
          <X size={28} />
        </button>

        <div className="p-8 sm:p-12 relative z-10">
          {status === 'success' ? (
            <div className="text-center py-10 animate-fade-in">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-8 border border-green-500/30">
                <CheckCircle2 size={40} className="text-green-500" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-black mb-4 text-white">{t.form.success_title}</h3>
              <p className="text-zinc-400 text-sm sm:text-base mb-10 leading-relaxed">{t.form.success_desc}</p>
              <button 
                onClick={onClose}
                className="w-full py-4 rounded-xl gradient-bg font-black tracking-widest uppercase text-sm"
              >
                {t.form.close}
              </button>
            </div>
          ) : (
            <>
              <h3 className="text-2xl sm:text-3xl font-black mb-2 text-white">{t.form.title}</h3>
              <p className="text-zinc-500 text-xs sm:text-sm mb-10 font-medium">{t.form.subtitle}</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2 ml-1">{t.form.label_name}</label>
                  <input 
                    required
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white focus:outline-none focus:border-red-500/50 transition-all placeholder:text-zinc-700"
                    placeholder="John Doe"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2 ml-1">{t.form.label_email}</label>
                    <input 
                      required
                      type="email"
                      value={formData.email}
                      onChange={e => setFormData({...formData, email: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white focus:outline-none focus:border-red-500/50 transition-all placeholder:text-zinc-700"
                      placeholder="email@empresa.com"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2 ml-1">{t.form.label_phone}</label>
                    <input 
                      required
                      type="tel"
                      value={formData.phone}
                      onChange={e => setFormData({...formData, phone: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white focus:outline-none focus:border-red-500/50 transition-all placeholder:text-zinc-700"
                      placeholder="+55 11 99999-9999"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2 ml-1">{t.form.label_comment}</label>
                  <textarea 
                    required
                    rows={4}
                    value={formData.comment}
                    onChange={e => setFormData({...formData, comment: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white focus:outline-none focus:border-red-500/50 transition-all placeholder:text-zinc-700 resize-none"
                    placeholder={t.form.placeholder_comment}
                  />
                </div>

                <button 
                  disabled={status === 'sending'}
                  className="w-full py-5 rounded-xl gradient-bg font-black tracking-widest uppercase text-xs sm:text-sm flex items-center justify-center gap-3 hover:scale-[1.02] active:scale-95 transition-all shadow-2xl shadow-red-500/20 disabled:opacity-50 disabled:hover:scale-100"
                >
                  {status === 'sending' ? (
                    <Loader2 className="animate-spin w-5 h-5" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                  {status === 'sending' ? t.form.sending : t.form.submit}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Navbar: React.FC<LanguageProps> = ({ lang, setLang, t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 w-full z-[100] transition-all duration-500 ${scrolled ? 'bg-black/95 backdrop-blur-xl border-b border-white/10 py-2' : 'bg-transparent py-4 md:py-6'}`}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 flex justify-between items-center" aria-label="Navegação Principal">
        <a href="#" className="flex items-center" title="DeFi Bank - Home">
          <img 
            src="https://i.imgur.com/VYool5N.png" 
            alt="Logo DeFi Bank Wallet" 
            className="h-9 sm:h-10 md:h-12 w-auto object-contain transition-all duration-300 drop-shadow-[0_0_10px_rgba(255,255,255,0.05)]" 
          />
        </a>

        <div className="hidden lg:flex items-center gap-5 xl:gap-8 font-bold text-[10px] tracking-[0.15em] uppercase text-zinc-400">
          <a href="#pix" className="hover:text-white transition-colors whitespace-nowrap">{t.nav.pix}</a>
          <a href="#cartao" className="hover:text-white transition-colors whitespace-nowrap">{t.nav.card}</a>
          <a href="#b2b" className="hover:text-white transition-colors whitespace-nowrap">{t.nav.b2b}</a>
          <a href="#token" className="hover:text-white transition-colors whitespace-nowrap">{t.nav.token}</a>
          <a href={LISTING_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors whitespace-nowrap">{t.nav.staking}</a>
          
          <div className="flex items-center border border-white/10 rounded-lg p-0.5 overflow-hidden ml-2">
            <button 
              onClick={() => setLang('pt')}
              className={`px-2 py-1 text-[8px] font-bold transition-all ${lang === 'pt' ? 'bg-white/10 text-white' : 'text-zinc-600 hover:text-zinc-400'}`}
              aria-label="Mudar para Português"
            >
              PT
            </button>
            <button 
              onClick={() => setLang('en')}
              className={`px-2 py-1 text-[8px] font-bold transition-all ${lang === 'en' ? 'bg-white/10 text-white' : 'text-zinc-600 hover:text-zinc-400'}`}
              aria-label="Change to English"
            >
              EN
            </button>
          </div>

          <a href={REDIRECT_URL} className="px-5 py-2.5 rounded-xl gradient-bg text-white font-black tracking-widest hover:brightness-110 transition-all shadow-lg shadow-red-500/10 active:scale-95 text-[10px] inline-block whitespace-nowrap ml-2">
            {t.nav.wallet}
          </a>
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <button 
            onClick={() => setLang(lang === 'pt' ? 'en' : 'pt')}
            className="text-[10px] font-black text-white/50 border border-white/10 rounded-md px-2 py-1"
          >
            {lang.toUpperCase()}
          </button>
          <button className="text-white p-2 relative z-[110]" onClick={() => setIsOpen(!isOpen)} aria-label="Abrir Menu">
            {isOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-[105] bg-black flex flex-col pt-24 px-8"
          >
            <div className="flex flex-col gap-8 text-base font-black tracking-[0.2em] uppercase text-left border-t border-white/5 pt-10">
              <a href="#pix" className="text-zinc-400 hover:text-white" onClick={() => setIsOpen(false)}>{translations[lang].nav.pix}</a>
              <a href="#cartao" className="text-zinc-400 hover:text-white" onClick={() => setIsOpen(false)}>{translations[lang].nav.card}</a>
              <a href="#b2b" className="text-zinc-400 hover:text-white" onClick={() => setIsOpen(false)}>{translations[lang].nav.b2b}</a>
              <a href="#token" className="text-zinc-400 hover:text-white" onClick={() => setIsOpen(false)}>{translations[lang].nav.token}</a>
              <a href={LISTING_URL} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-white" onClick={() => setIsOpen(false)}>{translations[lang].nav.staking}</a>
              
              <div className="mt-4">
                <a href={REDIRECT_URL} className="w-full py-5 rounded-2xl gradient-bg font-black tracking-widest text-center text-xs inline-block">
                  {translations[lang].nav.wallet}
                </a>
              </div>
            </div>

            <div className="mt-auto pb-12 flex justify-center gap-6">
               <a href={SOCIAL_LINKS.telegram} target="_blank" className="text-zinc-500 hover:text-white"><Send size={20} /></a>
               <a href={SOCIAL_LINKS.instagram} target="_blank" className="text-zinc-500 hover:text-white"><Instagram size={20} /></a>
               <a href={SOCIAL_LINKS.x} target="_blank" className="text-zinc-500 hover:text-white"><Twitter size={20} /></a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const Hero: React.FC<HeroProps> = ({ t, onSelectItem }) => {
  return (
    <section className="relative min-h-screen flex items-center pt-24 pb-12 md:py-20 overflow-hidden" aria-labelledby="hero-title">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/90 to-black"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className="text-center lg:text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-500 text-[8px] sm:text-[10px] font-black tracking-[0.2em] uppercase mb-6 mx-auto lg:mx-0">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
            </span>
            {t.hero.badge}
          </div>
          <h1 id="hero-title" className="text-3xl sm:text-5xl md:text-5xl lg:text-6xl font-black leading-[1.1] mb-6 tracking-tight text-white px-2 sm:px-0">
            {t.hero.title_1} <br className="hidden sm:block" />
            <span className="gradient-text">{t.hero.title_accent}</span> <br className="hidden sm:block" />
            {t.hero.title_2}
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-zinc-400 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed px-4 sm:px-0">
            {t.hero.desc}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-6 px-4 sm:px-0">
            <a href={REDIRECT_URL} className="w-full sm:w-auto px-10 py-4 rounded-xl gradient-bg text-sm font-black flex items-center justify-center gap-3 hover:scale-105 transition-all shadow-2xl shadow-red-500/30 group uppercase tracking-widest" title="Abrir carteira Web3 grátis">
              {t.hero.cta} <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <div className="flex items-center gap-2 text-zinc-500 font-bold text-[10px] sm:text-[11px] tracking-widest uppercase py-2">
              <ShieldCheck className="text-green-500 w-5 h-5" aria-hidden="true" /> {t.hero.custody}
            </div>
          </div>
        </div>

        <div className="relative mt-8 lg:mt-0 px-4 sm:px-0 min-h-[350px] sm:min-h-[450px] flex items-end justify-center">
          <div className="absolute inset-0 bg-red-600/5 blur-[120px] rounded-full opacity-40 pointer-events-none"></div>
          
          <div className="relative flex items-end justify-center w-full h-full pb-8 sm:pb-12">
            {mockups.map((item, index) => {
              const total = mockups.length;
              const mid = Math.floor(total / 2);
              const offset = index - mid;
              const rotation = offset * 5; 
              const xTranslation = offset * (typeof window !== 'undefined' && window.innerWidth < 768 ? 35 : 45); 
              
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 50, rotate: rotation }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05, duration: 0.6 }}
                  whileHover={{ 
                    y: typeof window !== 'undefined' && window.innerWidth < 768 ? -40 : -60, 
                    rotate: 0, 
                    scale: 1.05, 
                    zIndex: 100,
                    transition: { duration: 0.2 }
                  }}
                  onClick={() => onSelectItem(item)}
                  className="absolute cursor-pointer w-[120px] md:w-[180px] origin-bottom group"
                  style={{ 
                    x: `${xTranslation}px`,
                    zIndex: 10 + index
                  }}
                  title={`Ver detalhes: ${item.title}`}
                >
                  <div className="bg-zinc-900 border border-zinc-800 rounded-[1.2rem] md:rounded-[2rem] overflow-hidden shadow-2xl relative ring-1 ring-white/5 group-hover:ring-orange-500 transition-all duration-300">
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                      <div className="gradient-bg text-white text-[7px] md:text-[9px] font-black uppercase tracking-[0.2em] px-2.5 py-1.5 rounded-full flex items-center gap-2 shadow-lg whitespace-nowrap">
                        <Maximize2 size={8} /> INFO
                      </div>
                    </div>

                    <div className="aspect-[9/19.5] relative">
                      <img 
                        src={item.image} 
                        alt={`Screenshot do app DeFi Bank - ${item.title}`} 
                        className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-500" 
                        loading="lazy"
                      />
                      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black via-black/10 to-transparent flex flex-col justify-end p-2 md:p-4 group-hover:opacity-0 transition-opacity">
                        <span className="text-white font-black text-[7px] md:text-xs uppercase tracking-widest leading-none">{item.title}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="absolute -top-4 -right-2 p-3 glass-card rounded-xl border-white/10 backdrop-blur-3xl shadow-2xl animate-bounce hidden lg:block z-50">
             <div className="flex items-center gap-2">
               <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_6px_#22c55e]"></div>
               <p className="text-[10px] sm:text-xs font-black text-white">{t.hero.yield}</p>
             </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PixSection: React.FC<TranslationProps> = ({ t }) => {
  return (
    <section id="pix" className="py-16 md:py-28 relative bg-black" aria-labelledby="pix-title">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="order-2 lg:order-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 w-full">
            <div className="glass-card p-6 sm:p-8 rounded-[28px] sm:rounded-[40px] border-white/5 hover:border-red-500/20 transition-all flex flex-col items-center sm:items-start text-center sm:text-left">
              <Smartphone className="text-red-500 w-8 h-8 sm:w-10 sm:h-10 mb-4 sm:mb-6" aria-hidden="true" />
              <h4 className="text-lg sm:text-xl font-black mb-2 text-white">{t.pix.feature_1_title}</h4>
              <p className="text-zinc-500 text-xs sm:text-sm leading-relaxed">{t.pix.feature_1_desc}</p>
            </div>
            <div className="glass-card p-6 sm:p-8 rounded-[28px] sm:rounded-[40px] border-white/5 hover:border-orange-500/20 transition-all flex flex-col items-center sm:items-start text-center sm:text-left sm:mt-12">
              <Repeat className="text-orange-500 w-8 h-8 sm:w-10 sm:h-10 mb-4 sm:mb-6" aria-hidden="true" />
              <h4 className="text-lg sm:text-xl font-black mb-2 text-white">{t.pix.feature_2_title}</h4>
              <p className="text-zinc-500 text-xs sm:text-sm leading-relaxed">{t.pix.feature_2_desc}</p>
            </div>
          </div>
        </div>

        <div className="order-1 lg:order-2 text-center lg:text-left">
          <h2 id="pix-title" className="text-red-500 font-bold tracking-[0.2em] uppercase text-[9px] sm:text-[10px] mb-4">{t.pix.badge}</h2>
          <h3 className="text-2xl sm:text-4xl md:text-5xl font-black mb-6 leading-tight tracking-tight text-white">{t.pix.title} <br className="hidden sm:block" />{t.pix.title_2}</h3>
          <p className="text-sm sm:text-base text-zinc-400 mb-8 leading-relaxed max-w-lg mx-auto lg:mx-0 px-2 sm:px-0">
            {t.pix.desc}
          </p>
          <a href={REDIRECT_URL} className="w-full sm:w-auto px-8 py-4 rounded-xl gradient-bg text-[10px] sm:text-[11px] font-black tracking-widest uppercase shadow-xl hover:brightness-110 transition-all inline-block text-center" title="Comprar stablecoins com Pix agora">
            {t.pix.cta}
          </a>
        </div>
      </div>
    </section>
  );
};

const AssetsSection: React.FC<TranslationProps> = ({ t }) => {
  const assets = [
    { name: "Bitcoin", icon: "https://app.defibank.digital/cryptocurrency-icons/svg/color/btc.svg", desc: t.assets.items.btc },
    { name: "Ethereum", icon: "https://app.defibank.digital/cryptocurrency-icons/svg/color/eth.svg", desc: t.assets.items.eth },
    { name: "USDT", icon: "https://app.defibank.digital/cryptocurrency-icons/svg/color/usdt.svg", desc: t.assets.items.usdt },
    { name: "BNB", icon: "https://app.defibank.digital/cryptocurrency-icons/svg/color/bnb.svg", desc: t.assets.items.bnb },
    { name: "Ouro (XAUM)", icon: "https://www.dextools.io/resources/tokens/logos/3/bsc/0x23ae4fd8e7844cdbc97775496ebd0e8248656028.png?1733560735", desc: t.assets.items.xaum },
    { name: "BRZ", icon: "https://app.defibank.digital/cryptocurrency-icons/svg/color/brz.svg", desc: t.assets.items.brz },
    { name: "XRP", icon: "https://app.defibank.digital/cryptocurrency-icons/svg/color/xrp.svg", desc: t.assets.items.xrp },
    { name: "USDC", icon: "https://app.defibank.digital/cryptocurrency-icons/svg/color/usdc.svg", desc: t.assets.items.usdc },
    { name: "USD1", icon: "https://www.dextools.io/resources/tokens/logos/3/solana/USD1ttGY1N17NEEHLmELoaybftRBUSErhqYiQzvEmuB.png?1749297002", desc: t.assets.items.usd1 }
  ];

  return (
    <section className="py-20 md:py-32 relative bg-zinc-950 overflow-hidden" aria-labelledby="assets-title">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-4xl mx-auto mb-16 sm:mb-24 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-500 text-[10px] font-black tracking-[0.3em] uppercase mb-8">
            <Coins size={14} className="animate-pulse" aria-hidden="true" />
            {t.assets.badge}
          </div>
          <h2 id="assets-title" className="text-3xl sm:text-5xl md:text-6xl font-black mb-8 leading-tight tracking-tight text-white">
            {t.assets.title} <br className="hidden sm:block" />
            <span className="gradient-text">{t.assets.title_accent}</span>
          </h2>
          <p className="text-sm sm:text-lg text-zinc-400 font-medium leading-relaxed max-w-2xl mx-auto mb-12">
            {t.assets.desc}
          </p>
          
          <div className="inline-block px-6 py-3 rounded-2xl bg-white/5 border border-white/10 shadow-2xl backdrop-blur-xl">
             <div className="flex items-center gap-3">
                <TrendingUp className="text-green-500 w-5 h-5" aria-hidden="true" />
                <span className="text-white font-black text-xs sm:text-sm tracking-widest uppercase">{t.assets.plus}</span>
             </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-4 sm:gap-6 relative z-10">
          {assets.map((asset, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              whileHover={{ y: -8, scale: 1.05 }}
              className="group glass-card p-6 rounded-[28px] border-white/5 flex flex-col items-center justify-center text-center hover:border-red-500/30 transition-all duration-300 shadow-xl"
            >
              <div className="relative mb-4">
                <div className="absolute inset-0 bg-white/10 blur-[20px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <img src={asset.icon} alt={`Criptomoeda ${asset.name}`} className="w-12 h-12 sm:w-14 sm:h-14 relative z-10 drop-shadow-2xl" loading="lazy" />
              </div>
              <h4 className="text-white font-black text-[10px] sm:text-[11px] uppercase tracking-widest leading-none mb-1">{asset.name}</h4>
              <p className="text-zinc-600 text-[8px] sm:text-[9px] font-bold uppercase tracking-tighter opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">{asset.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 sm:mt-24 text-center">
           <a href={REDIRECT_URL} className="w-full sm:w-auto px-12 py-5 rounded-[22px] gradient-bg text-base font-black flex items-center justify-center gap-4 hover:scale-105 transition-all shadow-[0_20px_60px_rgba(239,68,68,0.3)] mx-auto uppercase tracking-[0.2em]" title="Diversificar portfólio em dólar digital">
             {t.assets.cta} <ArrowRight className="w-6 h-6" aria-hidden="true" />
           </a>
        </div>
      </div>
    </section>
  );
};

const CardSection: React.FC<TranslationProps> = ({ t }) => {
  return (
    <section id="cartao" className="py-16 md:py-28 bg-zinc-950/50" aria-labelledby="card-title">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="text-center lg:text-left">
          <h2 id="card-title" className="text-orange-500 font-bold tracking-[0.2em] uppercase text-[9px] sm:text-[10px] mb-4">{t.card.badge}</h2>
          <h3 className="text-2xl sm:text-4xl md:text-5xl font-black mb-6 leading-tight tracking-tight text-white">{t.card.title} <br className="hidden sm:block" /><span className="gradient-text">{t.card.title_accent}</span></h3>
          <p className="text-sm sm:text-base text-zinc-400 mb-8 leading-relaxed max-w-lg mx-auto lg:mx-0 px-2 sm:px-0">
            {t.card.desc}
          </p>
          
          <div className="grid grid-cols-2 gap-6 sm:gap-10 mb-8 sm:mb-10 max-w-xs sm:max-w-sm mx-auto lg:mx-0">
            <div className="flex flex-col">
              <span className="text-2xl sm:text-4xl font-black text-white">0%</span>
              <span className="text-zinc-500 font-bold uppercase text-[8px] sm:text-[10px] tracking-widest">{t.card.iof_label}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-2xl sm:text-4xl font-black text-white">3%</span>
              <span className="text-zinc-500 font-bold uppercase text-[8px] sm:text-[10px] tracking-widest">{t.card.cashback_label}</span>
            </div>
          </div>
          
          <a href={REDIRECT_URL} className="w-full sm:w-auto px-8 py-4 rounded-xl border-2 border-red-500 text-red-500 text-[10px] sm:text-[11px] font-black tracking-widest uppercase hover:bg-red-500 hover:text-white transition-all shadow-xl inline-block text-center" title="Solicitar cartão cripto internacional">
            {t.card.cta}
          </a>
        </div>

        <div className="relative flex justify-center lg:justify-end mt-8 lg:mt-0 px-4 sm:px-0">
          <div className="relative group perspective-1000">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 via-red-500/20 to-pink-500/20 blur-[80px] sm:blur-[120px] rounded-full opacity-50 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
            
            <div className="relative transition-all duration-700 ease-out transform-gpu group-hover:rotate-y-12 group-hover:rotate-x-12 group-hover:scale-110">
              <img 
                src="https://i.imgur.com/Cj2gn9n.png" 
                alt="Novo Cartão Visa DeFi Bank Black" 
                className="w-full max-w-[320px] sm:max-w-[440px] rounded-[24px] shadow-[0_30px_100px_rgba(0,0,0,0.9)] border border-white/10" 
                loading="lazy"
              />
              <div className="absolute inset-0 rounded-[24px] bg-gradient-to-tr from-transparent via-white/5 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
            </div>
          </div>

          <div className="absolute -bottom-4 right-4 sm:-bottom-6 sm:right-0 p-3 sm:p-4 glass-card rounded-xl sm:rounded-2xl border-white/10 backdrop-blur-3xl shadow-xl animate-float hidden sm:block z-20">
             <div className="flex items-center gap-3">
               <div className="px-3 py-1.5 bg-white rounded-md flex items-center justify-center">
                <img src="https://images.seeklogo.com/logo-png/25/1/visa-logo-png_seeklogo-256106.png" className="h-5" alt="Bandeira Visa" />
               </div>
               <span className="font-black text-[9px] sm:text-[10px] text-white uppercase tracking-widest">{t.card.badge_float}</span>
             </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const TokenSection: React.FC<TranslationProps> = ({ t }) => {
  const [copied, setCopied] = useState(false);

  const copyContract = () => {
    navigator.clipboard.writeText(DBK_CONTRACT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const pillars = [
    { title: t.token.p1_title, phrase: t.token.p1_phrase, desc: t.token.p1_desc, icon: BarChart3 },
    { title: t.token.p2_title, phrase: t.token.p2_phrase, desc: t.token.p2_desc, icon: Globe },
    { title: t.token.p3_title, phrase: t.token.p3_phrase, desc: t.token.p3_desc, icon: Activity },
    { title: t.token.p4_title, phrase: t.token.p4_phrase, desc: t.token.p4_desc, icon: Zap }
  ];

  return (
    <section id="token" className="py-16 md:py-28 overflow-hidden bg-black" aria-labelledby="token-title">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-4xl mx-auto mb-16 sm:mb-24">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-500 text-[9px] font-black tracking-widest uppercase mb-8">
            {t.token.badge}
          </div>
          <h3 id="token-title" className="text-3xl sm:text-5xl md:text-6xl font-black mb-8 leading-tight tracking-tight text-white">
            {t.token.title} <br className="hidden sm:block" />
            <span className="gradient-text">{t.token.subtitle}</span>
          </h3>
          <p className="text-sm sm:text-lg text-zinc-500 font-medium leading-relaxed max-w-2xl mx-auto">
            {t.token.lead}
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-start mb-20 sm:mb-32">
          <div className="grid sm:grid-cols-2 gap-8 sm:gap-12">
            {pillars.map((pillar, i) => (
              <div key={i} className="group flex flex-col items-center sm:items-start text-center sm:text-left">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-[24px] bg-zinc-900 border border-white/5 flex items-center justify-center mb-8 group-hover:border-red-500/30 group-hover:scale-110 transition-all duration-500 shadow-2xl">
                  <pillar.icon className="text-red-500 w-8 h-8 sm:w-10 sm:h-10" aria-hidden="true" />
                </div>
                <h5 className="text-lg sm:text-xl font-black mb-3 text-white uppercase tracking-tight">{pillar.title}</h5>
                <p className="text-zinc-300 font-bold text-sm sm:text-base mb-3 leading-snug">
                  {pillar.phrase}
                </p>
                <p className="text-zinc-600 text-[11px] sm:text-[13px] leading-relaxed font-medium">
                  {pillar.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-10">
            <div className="relative aspect-square max-w-[400px] mx-auto lg:mx-0 w-full flex items-center justify-center">
                <div className="absolute inset-0 bg-red-600/5 rounded-full blur-[120px] animate-pulse"></div>
                <div className="relative w-full h-full glass-card rounded-[64px] border-white/10 shadow-[0_50px_100px_rgba(0,0,0,0.8)] flex flex-col items-center justify-center animate-float group overflow-hidden">
                   <div className="absolute inset-0 bg-gradient-to-tr from-red-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                   <img src="https://i.imgur.com/sd3LZoV.png" className="w-32 sm:w-48 md:w-64 h-auto drop-shadow-2xl z-10" alt="Token Físico $DBK Representativo" loading="lazy" />
                </div>
            </div>

            <div className="glass-card p-6 sm:p-8 rounded-[32px] border-white/10 flex flex-col sm:flex-row items-center justify-between gap-6 max-w-md mx-auto lg:mx-0">
                <div className="flex flex-col gap-1 overflow-hidden w-full text-left">
                  <span className="text-[8px] font-black text-zinc-600 uppercase tracking-[0.4em] mb-1">{t.token.contract_label}</span>
                  <span className="font-mono text-zinc-300 text-[10px] sm:text-[12px] truncate">{DBK_CONTRACT}</span>
                </div>
                <button 
                  onClick={copyContract}
                  className="w-full sm:w-auto px-6 py-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all flex items-center justify-center gap-3 border border-white/10 active:scale-95 shadow-xl group"
                >
                  {copied ? <CheckCircle2 className="text-green-500 w-4 h-4" /> : <Copy className="w-4 h-4 text-zinc-400 group-hover:text-red-500" />}
                  <span className="font-black uppercase text-[10px] tracking-widest text-white">{copied ? t.token.copied : t.token.copy}</span>
                </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const SecuritySection: React.FC<TranslationProps> = ({ t }) => {
  return (
    <section id="seguranca" className="py-16 md:py-28 bg-black border-y border-white/5" aria-labelledby="security-title">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
        <div className="text-center lg:text-left">
          <h2 id="security-title" className="text-red-500 font-bold tracking-[0.2em] uppercase text-[9px] sm:text-[10px] mb-4">{t.security.badge}</h2>
          <h3 className="text-2xl sm:text-4xl md:text-5xl font-black mb-8 leading-tight tracking-tight text-white">{t.security.title}</h3>
          <p className="text-sm sm:text-base text-zinc-400 mb-10 sm:mb-12 leading-relaxed max-w-lg mx-auto lg:mx-0 px-2 sm:px-0">
            {t.security.desc}
          </p>
          
          <div className="grid gap-6 sm:gap-8 text-left max-w-2xl mx-auto lg:mx-0 px-4 sm:px-0">
            {[
              { title: t.security.item_1_title, desc: t.security.item_1_desc },
              { title: t.security.item_2_title, desc: t.security.item_2_desc },
              { title: t.security.item_3_title, desc: t.security.item_3_desc }
            ].map((item, i) => (
              <div key={i} className="flex gap-4 sm:gap-5 items-start p-5 sm:p-6 rounded-3xl bg-white/5 border border-white/5 hover:border-white/10 transition-all shadow-md group">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-red-500/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <ShieldCheck className="text-red-500 w-6 h-6 sm:w-7 sm:h-7" aria-hidden="true" />
                </div>
                <div>
                  <h5 className="text-white font-black text-sm sm:text-base mb-1 uppercase tracking-wider">{item.title}</h5>
                  <p className="text-zinc-500 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative flex justify-center mt-12 lg:mt-0 px-4 sm:px-0">
           <div className="glass-card rounded-[40px] sm:rounded-[64px] w-full max-w-[280px] sm:max-w-[400px] aspect-square flex flex-col items-center justify-center border-white/10 overflow-hidden relative group shadow-2xl">
              <div className="absolute inset-0 bg-red-500/5 group-hover:bg-red-500/10 transition-all duration-700"></div>
              <div className="relative z-10 w-20 h-20 sm:w-32 sm:h-32 rounded-[32px] sm:rounded-[40px] gradient-bg flex items-center justify-center shadow-2xl mb-8 group-hover:scale-110 transition-transform duration-500">
                <Lock size={40} className="text-white sm:w-[56px] sm:h-[56px]" aria-hidden="true" />
              </div>
              <span className="text-lg sm:text-2xl font-black tracking-[0.1em] uppercase text-white text-center px-6 leading-tight">{t.security.shield}</span>
              <p className="mt-4 sm:mt-6 text-zinc-500 text-[9px] sm:text-[11px] font-bold tracking-[0.3em] uppercase text-center">{t.security.cert}</p>
           </div>
        </div>
      </div>
    </section>
  );
};

const Footer: React.FC<TranslationProps> = ({ t }) => {
  return (
    <footer className="py-20 md:py-28 bg-black border-t border-white/5" aria-label="Rodapé Otimizado">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 mb-20">
          <div className="lg:col-span-5 flex flex-col items-start">
            <img src="https://i.imgur.com/VYool5N.png" alt="Logo DeFi Bank Rodapé" className="h-12 sm:h-14 w-auto mb-8" loading="lazy" />
            <p className="text-zinc-500 text-sm sm:text-base leading-relaxed max-w-md">
              {t.footer.desc}
            </p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-8">
            <div className="flex flex-col gap-6">
              <h5 className="text-white font-black text-xs sm:text-sm tracking-[0.2em] uppercase font-tech pb-2 border-b border-white/10">{t.footer.navigate}</h5>
              <div className="flex flex-col gap-4 text-zinc-500 text-xs sm:text-sm font-medium">
                <a href="#pix" className="hover:text-red-500 transition-colors uppercase tracking-widest" title="Comprar stablecoins via Pix">{t.footer.links.pix}</a>
                <a href="#cartao" className="hover:text-red-500 transition-colors uppercase tracking-widest" title="Vantagens do Cartão Visa DeFi">{t.footer.links.card}</a>
              </div>
            </div>
            <div className="flex flex-col gap-6">
              <h5 className="text-transparent pointer-events-none select-none font-black text-xs sm:text-sm tracking-[0.2em] uppercase font-tech pb-2 border-b border-transparent sm:border-white/10 hidden sm:block">.</h5>
              <div className="flex flex-col gap-4 text-zinc-500 text-xs sm:text-sm font-medium">
                <a href="#b2b" className="hover:text-red-500 transition-colors uppercase tracking-widest" title="Soluções Institucionais Blockchain">{t.footer.links.b2b}</a>
              </div>
            </div>
            <div className="flex flex-col gap-6">
              <h5 className="text-transparent pointer-events-none select-none font-black text-xs sm:text-sm tracking-[0.2em] uppercase font-tech pb-2 border-b border-transparent sm:border-white/10 hidden sm:block">.</h5>
              <div className="flex flex-col gap-4 text-zinc-500 text-xs sm:text-sm font-medium">
                <a href="#token" className="hover:text-red-500 transition-colors uppercase tracking-widest" title="Saiba mais sobre o token $DBK">{t.footer.links.token}</a>
              </div>
            </div>
            <div className="flex flex-col gap-6">
              <h5 className="text-transparent pointer-events-none select-none font-black text-xs sm:text-sm tracking-[0.2em] uppercase font-tech pb-2 border-b border-transparent sm:border-white/10 hidden sm:block">.</h5>
              <div className="flex flex-col gap-4 text-zinc-500 text-xs sm:text-sm font-medium">
                <a href={LISTING_URL} target="_blank" rel="noopener noreferrer" className="hover:text-red-500 transition-colors uppercase tracking-widest" title="Listagem estratégica de tokens">{t.footer.links.staking}</a>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-10 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="flex gap-4 sm:gap-6">
            <a href={SOCIAL_LINKS.telegram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 sm:w-12 sm:h-12 bg-zinc-900 rounded-full flex items-center justify-center text-zinc-400 hover:bg-red-500 hover:text-white transition-all shadow-lg border border-white/5" title="Telegram Oficial">
              <Send size={20} />
            </a>
            <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 sm:w-12 sm:h-12 bg-zinc-900 rounded-full flex items-center justify-center text-zinc-400 hover:bg-pink-500 hover:text-white transition-all shadow-lg border border-white/5" title="Instagram Oficial">
              <Instagram size={20} />
            </a>
            <a href={SOCIAL_LINKS.x} target="_blank" rel="noopener noreferrer" className="w-10 h-10 sm:w-12 sm:h-12 bg-zinc-900 rounded-full flex items-center justify-center text-zinc-400 hover:bg-zinc-800 hover:text-white transition-all shadow-lg border border-white/5" title="X (Twitter) Oficial">
              <Twitter size={20} />
            </a>
            <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noopener noreferrer" className="w-10 h-10 sm:w-12 sm:h-12 bg-zinc-900 rounded-full flex items-center justify-center text-zinc-400 hover:bg-zinc-800 hover:text-white transition-all shadow-lg border border-white/5" title="TikTok Oficial">
              <TikTokIcon className="w-5 h-5" />
            </a>
            <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 sm:w-12 sm:h-12 bg-zinc-900 rounded-full flex items-center justify-center text-zinc-400 hover:bg-red-600 hover:text-white transition-all shadow-lg border border-white/5" title="Canal YouTube Oficial">
              <Youtube size={20} />
            </a>
          </div>
          <p className="text-[10px] sm:text-[11px] font-bold text-zinc-700 tracking-[0.3em] uppercase text-center md:text-right">
            {t.footer.rights}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default function App() {
  const [lang, setLang] = useState<Language>('pt');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MockupItem | null>(null);
  const t = translations[lang] as TranslationSchema;

  // SEO: Injeção Dinâmica de Structured Data (JSON-LD)
  const structuredData = useMemo(() => {
    return {
      "@context": "https://schema.org",
      "@type": "FinancialService",
      "name": "DeFi Bank Wallet",
      "alternateName": "DeFi Bank",
      "description": "A melhor carteira Web3 do Brasil. Autocustódia total, Pix para USDT instantâneo e cartão Visa sem IOF.",
      "url": "https://defibank.digital",
      "logo": "https://i.imgur.com/pkU9hga.png",
      "sameAs": [
        SOCIAL_LINKS.instagram,
        SOCIAL_LINKS.x,
        SOCIAL_LINKS.youtube
      ],
      "offers": {
        "@type": "Offer",
        "description": "Conversão Pix para USDT com taxa zero e rendimento anual em stablecoins."
      }
    };
  }, []);

  useEffect(() => {
    // Injetar script JSON-LD no head para o robô do Google
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify(structuredData);
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, [structuredData]);

  return (
    <div className="min-h-screen bg-black text-white selection:bg-red-500 selection:text-white overflow-x-hidden w-full">
      <Navbar lang={lang} setLang={setLang} t={t} />
      
      <main>
        <Hero t={t} onSelectItem={setSelectedItem} />
        
        <section className="bg-zinc-950/80 py-12 sm:py-20 border-y border-white/10" aria-label="Estatísticas de Crescimento">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 md:gap-16">
            <div className="text-center">
              <p className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-2 gradient-text">R$ 1M +</p>
              <p className="text-zinc-600 text-[8px] sm:text-[10px] font-black uppercase tracking-[0.2em] leading-none">{t.stats.remittances}</p>
            </div>
            <div className="text-center">
              <p className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-2 gradient-text">2000+</p>
              <p className="text-zinc-600 text-[8px] sm:text-[10px] font-black uppercase tracking-[0.2em] leading-none">{t.stats.users}</p>
            </div>
            <div className="text-center">
              <p className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-2 gradient-text">27M+</p>
              <p className="text-zinc-600 text-[8px] sm:text-[10px] font-black uppercase tracking-[0.2em] leading-none">{t.stats.stake}</p>
            </div>
            <div className="text-center">
              <p className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-2 gradient-text">0.0%</p>
              <p className="text-zinc-600 text-[8px] sm:text-[10px] font-black uppercase tracking-[0.2em] leading-none">{t.stats.fee}</p>
            </div>
          </div>
        </section>

        <PixSection t={t} />
        <AssetsSection t={t} />
        <CardSection t={t} />
        
        <section className="py-16 md:py-28 relative bg-black border-t border-white/5" aria-label="Nossos Parceiros">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-red-500/80 font-bold tracking-[0.4em] uppercase text-[10px] sm:text-[11px] mb-4 block">
                {t.partners.badge}
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-6 leading-tight tracking-tight text-white">
                {t.partners.title}
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-zinc-500 font-medium leading-relaxed px-2 sm:px-0">
                {t.partners.desc}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 md:gap-12 items-center justify-items-center">
              {[
                { name: "BNB Chain", logo: "https://cdn.brandfetch.io/id8njqSNGQ/theme/dark/logo.svg?c=1dxbfHSJFAPEGdCLU4o5B" },
                { name: "ChainGPT", logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT4nwjbuxiOyCNKaVULzRsfRE2DAZiY1zmBZQ&s" },
                { name: "KuCoin", logo: "https://www.dextools.io/resources/tokens/logos/1/ether/0xf34960d9d60be18cc1d5afc1a6f012a723a28811.png?58e8accbe57e6e6789284537f834ed517fb14351" },
                { name: "Uniswap", logo: "https://cdn.brandfetch.io/idoYtBNi2C/theme/dark/symbol.svg?c=1dxbfHSJFAPEGdCLU4o5B" },
                { name: "OKX", logo: "https://www.dextools.io/resources/tokens/logos/solana/A5gG2vRx8prCUS2wKnUrffYzbkhrBA3yCxzNLWJWpump.jpg?1738553327984" }
              ].map((partner, i) => (
                <div key={i} className="group transition-all duration-300">
                  <img 
                    src={partner.logo} 
                    alt={`Logo da parceira ${partner.name}`} 
                    className="h-8 sm:h-10 md:h-12 w-auto object-contain grayscale opacity-40 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="b2b" className="py-16 md:py-28 relative" aria-labelledby="b2b-title">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-20">
              <h2 id="b2b-title" className="text-zinc-500 font-bold tracking-[0.3em] uppercase text-[9px] sm:text-[11px] mb-4">{t.b2b.badge}</h2>
              <h3 className="text-2xl sm:text-4xl md:text-5xl font-black mb-6 leading-tight tracking-tight text-white">{t.b2b.title}</h3>
              <p className="text-sm sm:text-base text-zinc-500 font-medium leading-relaxed px-2 sm:px-0">{t.b2b.desc}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mb-16 sm:mb-20">
              {[
                { title: t.b2b.sol_1_title, desc: t.b2b.sol_1_desc, cta: t.b2b.sol_1_cta, icon: Headset, color: "text-red-500" },
                { title: t.b2b.sol_2_title, desc: t.b2b.sol_2_desc, cta: t.b2b.sol_2_cta, icon: Webhook, color: "text-orange-500" },
                { title: t.b2b.sol_3_title, desc: t.b2b.sol_3_desc, cta: t.b2b.sol_3_cta, icon: Building2, color: "text-pink-500" },
                { title: t.b2b.sol_4_title, desc: t.b2b.sol_4_desc, cta: t.b2b.sol_4_cta, icon: Rocket, color: "text-purple-500" }
              ].map((sol, i) => (
                <article key={i} className="glass-card p-6 sm:p-10 rounded-[28px] sm:rounded-[48px] border-white/5 hover:border-white/10 transition-all group flex flex-col justify-between text-center sm:text-left items-center sm:items-start shadow-md">
                  <div>
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-white/5 flex items-center justify-center mb-6 sm:mb-8 border border-white/5 group-hover:border-white/20 transition-all">
                      <sol.icon className={`${sol.color} w-6 h-6 sm:w-8 h-8`} aria-hidden="true" />
                    </div>
                    <h4 className="text-xl sm:text-2xl font-black mb-3 text-white">{sol.title}</h4>
                    <p className="text-zinc-500 text-xs sm:text-sm leading-relaxed mb-8 sm:mb-10">{sol.desc}</p>
                  </div>
                 <a href="https://wa.me/5511913470981?text=Quero%20Saber%20mais%20sobre%20as%20integra%C3%A7%C3%B5es%20e%20produtos%20empresariais%20da%20Defi%20Bank" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-white group-hover:text-red-500 transition-colors">
  {sol.cta} <ChevronRight className="w-4 h-4" />
</a>
                </article>
              ))}
            </div>

            <div className="p-8 sm:p-20 rounded-[32px] sm:rounded-[64px] bg-gradient-to-br from-zinc-900/60 to-black border border-white/10 text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-full bg-red-600/5 blur-[100px] pointer-events-none"></div>
              <div className="relative z-10">
                <h4 className="text-xl sm:text-4xl font-black mb-6 sm:mb-8 tracking-tight text-white leading-tight">{t.b2b.final_title}</h4>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mt-8 sm:mt-10">
                 <a href="https://wa.me/5511913470981?text=Quero%20Saber%20mais%20sobre%20as%20integra%C3%A7%C3%B5es%20e%20produtos%20empresariais%20da%20Defi%20Bank" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto px-10 py-4 rounded-xl gradient-bg text-[10px] font-black tracking-widest uppercase shadow-xl shadow-red-500/20 active:scale-95 transition-all inline-block text-center">
  {t.b2b.final_cta}
</a>
                 <a href="https://wa.me/5511913470981?text=Quero%20Saber%20mais%20sobre%20as%20integra%C3%A7%C3%B5es%20e%20produtos%20empresariais%20da%20Defi%20Bank" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto px-10 py-4 rounded-xl bg-white/5 border border-white/10 text-[10px] font-black tracking-widest uppercase hover:bg-white/10 transition-all inline-block text-center">
  {t.b2b.final_demo}
</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <TokenSection t={t} />
        <SecuritySection t={t} />
        
        <section className="py-16 md:py-28 relative text-center bg-black overflow-hidden" aria-label="Chamada Final">
          <div className="max-w-4xl mx-auto px-6 relative z-10">
            <img src="https://i.imgur.com/sd3LZoV.png" className="h-16 sm:h-24 w-auto mx-auto mb-10 sm:mb-12" alt="Ícone DeFi Bank Final" loading="lazy" />
            <h2 className="text-3xl sm:text-5xl md:text-7xl font-black mb-8 sm:mb-12 leading-[1.1] tracking-tight text-white">
              {t.final.title} <br className="hidden sm:block" />
              <span className="gradient-text">{t.final.title_accent}</span>
            </h2>
            <p className="text-sm sm:text-lg md:text-2xl text-zinc-400 mb-12 sm:mb-16 leading-relaxed max-w-3xl mx-auto px-2 sm:px-0">
              {t.final.desc}
            </p>
            <div className="px-4 sm:px-0">
              <a href={REDIRECT_URL} className="w-full sm:w-auto px-10 sm:px-16 py-5 sm:py-7 rounded-[24px] sm:rounded-[28px] gradient-bg text-base sm:text-2xl font-black shadow-3xl shadow-red-500/30 hover:scale-105 transition-all uppercase tracking-[0.2em] inline-block text-center" title="Começar jornada Web3 agora">
                {t.final.cta}
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer t={t} />
      
      <ContactModal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} t={t} />

      <AnimatePresence>
        {selectedItem && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/98 backdrop-blur-2xl"
            onClick={() => setSelectedItem(null)}
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-zinc-950 rounded-[2rem] md:rounded-[3.5rem] border border-white/10 max-w-5xl w-full overflow-hidden flex flex-col md:flex-row relative shadow-2xl"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <button 
                onClick={() => setSelectedItem(null)}
                className="absolute top-4 right-4 md:top-8 md:right-8 z-30 bg-white/10 p-2 md:p-3 rounded-full text-white hover:gradient-bg transition-colors"
                aria-label="Fechar Detalhes"
              >
                <X size={20} />
              </button>
              
              <div className="md:w-[40%] p-4 bg-black flex justify-center items-center">
                <img src={selectedItem.image} alt={`Visualização ampliada: ${selectedItem.title}`} className="max-h-[50vh] md:max-h-[70vh] object-contain rounded-[1.5rem]" />
              </div>
              
              <div className="md:w-[60%] p-6 md:p-16 flex flex-col justify-center text-left">
                <span className="text-orange-500 font-black text-[10px] uppercase tracking-[0.3em] mb-2 block">USER EXPERIENCE</span>
                <h3 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-4 uppercase tracking-tighter leading-none italic">{selectedItem.title}</h3>
                <p className="text-zinc-400 text-base md:text-xl mb-8 leading-relaxed font-medium">{selectedItem.description}</p>
                <div className="bg-white/5 border border-white/10 p-6 md:p-8 rounded-[1.5rem] md:rounded-[2.5rem]">
                  <h4 className="gradient-text font-black mb-2 uppercase tracking-[0.2em] text-[10px]">Vantagem Competitiva</h4>
                  <p className="text-white text-lg md:text-2xl font-bold italic leading-tight">"{selectedItem.benefit}"</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <button 
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 w-11 h-11 sm:w-14 sm:h-14 bg-zinc-900/90 backdrop-blur-2xl border border-white/20 rounded-2xl sm:rounded-3xl flex items-center justify-center hover:bg-red-500 hover:border-red-500 transition-all z-40 group shadow-3xl"
        aria-label="Voltar ao topo da página"
      >
        <ChevronDown className="w-6 h-6 sm:w-8 sm:h-8 rotate-180 group-hover:-translate-y-1 transition-transform text-zinc-400 group-hover:text-white" />
      </button>
    </div>
  );
}
